import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RESP_STATUSES, RESP_STATUS_LABELS, RESP_STATUS_COLORS, formatDate } from "@/lib/labels";
import ResponsibleDialog from "@/components/ResponsibleDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCheck, PlusCircle, Search, Pencil, Trash2, History, Repeat, User, Inbox } from "lucide-react";

const ALL = "__all__";
function SubstituteDialog({ open, onOpenChange, target, responsibles, onSaved }) {
  const [subId, setSubId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => {
    if (open && target) {
      setSubId(target.substitute?.responsible_id || "");
      setStart(target.substitute?.start?.slice(0, 10) || "");
      setEnd(target.substitute?.end?.slice(0, 10) || "");
    }
  }, [open, target]);
  const save = async () => {
    if (!subId || !start || !end) return toast.error("Preencha substituto e período de vigência");
    try {
      await api.put(`/responsibles/${target.id}/substitute`, { responsible_id: subId, start: `${start}T00:00:00`, end: `${end}T23:59:59` });
      toast.success("Substituto temporário definido");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const clear = async () => {
    await api.delete(`/responsibles/${target.id}/substitute`);
    toast.success("Substituição removida");
    onOpenChange(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="substitute-dialog">
        <DialogHeader><DialogTitle className="font-heading">Substituto temporário — {target?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Substituto</Label>
            <Select value={subId} onValueChange={setSubId}>
              <SelectTrigger data-testid="substitute-select"><SelectValue placeholder="Selecione o responsável substituto" /></SelectTrigger>
              <SelectContent>
                {responsibles.filter((r) => r.id !== target?.id && r.status === "ativo").map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Início da vigência</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="substitute-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fim da vigência</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="substitute-end-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={save} data-testid="substitute-save-btn">Salvar Substituição</Button>
            {target?.substitute && (
              <Button variant="outline" onClick={clear} data-testid="substitute-clear-btn">Remover</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, onOpenChange, target }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="resp-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Histórico — {target?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
          {(target?.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="rounded-md bg-muted/50 px-3.5 py-2.5" data-testid="resp-history-entry">
              <p className="text-sm">{h.detail}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{h.user} · {formatDate(h.timestamp)}</p>
            </div>
          ))}
          {(!target?.history || target.history.length === 0) && <p className="text-sm text-muted-foreground">Sem registros.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Responsibles() {
  const { user, can } = useAuth();
  // Mesmas ações que as rotas /responsibles exigem (responsaveis.<ação>).
  const canCreate = can("responsaveis", "create");
  const canEdit = can("responsaveis", "edit");
  const canDelete = can("responsaveis", "delete");
  const canManage = canEdit || canDelete;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [clientFilter, setClientFilter] = useState(ALL);
  const [tree, setTree] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [subTarget, setSubTarget] = useState(null);
  const [histTarget, setHistTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== ALL) params.status = statusFilter;
    if (clientFilter !== ALL) params.client_id = clientFilter;
    api.get("/responsibles", { params }).then(({ data }) => setList(data)).finally(() => setLoading(false));
  }, [search, statusFilter, clientFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/hierarchy/tree").then(({ data }) => setTree(data));
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
  }, []);

  const names = {};
  tree.forEach((c) => {
    names[c.id] = c.name;
    (c.units || []).forEach((u) => {
      names[u.id] = u.name;
      (u.areas || []).forEach((a) => {
        names[a.id] = a.name;
        (a.subareas || []).forEach((s) => { names[s.id] = s.name; });
      });
    });
  });
  companies.forEach((c) => { names[c.id] = c.name; });
  const byId = Object.fromEntries(list.map((r) => [r.id, r]));

  const assignmentLabel = (a) => {
    const parts = [a.company_id, a.client_id, a.unit_id, a.area_id, a.subarea_id].filter(Boolean).map((id) => names[id] || "?");
    return parts.length ? parts.join(" › ") : "Todos os locais";
  };

  const remove = async (r) => {
    if (!window.confirm(`Excluir o responsável ${r.name}?`)) return;
    try {
      await api.delete(`/responsibles/${r.id}`);
      toast.success("Responsável excluído");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="responsibles-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <UserCheck size={26} className="text-primary" /> Responsáveis
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mapeamento de responsáveis por local — atribuição automática nas ocorrências e escalonamento de SLA.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-responsible-btn">
            <PlusCircle size={16} className="mr-2" /> Novo Responsável
          </Button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, cargo, função, e-mail ou matrícula..."
            className="pl-9" data-testid="responsibles-search-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full lg:w-40" data-testid="responsibles-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os status</SelectItem>
              {RESP_STATUSES.map((s) => <SelectItem key={s} value={s}>{RESP_STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full lg:w-48" data-testid="responsibles-filter-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os clientes</SelectItem>
              {tree.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-muted/40">
                <th className="px-4 py-3 font-medium">Responsável</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Contato</th>
                <th className="px-4 py-3 font-medium">Vínculos</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Superior</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Substituto</th>
                {canManage && <th className="px-4 py-3 font-medium text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Carregando...</td></tr>}
              {!loading && list.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  <Inbox size={28} className="mx-auto mb-2 opacity-40" />Nenhum responsável cadastrado.
                </td></tr>
              )}
              {!loading && list.map((r) => (
                <tr key={r.id} className="border-b border-border/50 last:border-0 hover:bg-accent/40 transition-colors" data-testid={`responsible-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center overflow-hidden shrink-0">
                        {r.photo ? <img src={r.photo} alt={r.name} className="w-full h-full object-cover" /> : <User size={16} />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{[r.position, r.function].filter(Boolean).join(" · ") || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs">{r.email || "—"}</p>
                    <p className="text-xs text-muted-foreground">{r.whatsapp || r.phone || ""}</p>
                  </td>
                  <td className="px-4 py-3">
                    {(r.assignments || []).length === 0 ? (
                      <span className="text-xs text-muted-foreground">Sem vínculos</span>
                    ) : (
                      <div className="space-y-0.5 max-w-64">
                        {r.assignments.slice(0, 2).map((a, i) => (
                          <p key={i} className="text-xs text-muted-foreground truncate">{assignmentLabel(a)}</p>
                        ))}
                        {r.assignments.length > 2 && <p className="text-[10px] text-primary">+{r.assignments.length - 2} vínculo(s)</p>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                    {r.superior_id ? (byId[r.superior_id]?.name || "—") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={RESP_STATUS_COLORS[r.status] || ""} data-testid={`resp-status-badge-${r.id}`}>
                      {RESP_STATUS_LABELS[r.status] || r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {r.substitute?.responsible_id ? (
                      <div className="text-xs">
                        <p className="font-medium">{byId[r.substitute.responsible_id]?.name || "—"}</p>
                        <p className="text-muted-foreground">{r.substitute.start?.slice(0, 10)} → {r.substitute.end?.slice(0, 10)}</p>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar"
                            onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`resp-edit-btn-${r.id}`}>
                            <Pencil size={14} />
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Substituto temporário"
                            onClick={() => setSubTarget(r)} data-testid={`resp-substitute-btn-${r.id}`}>
                            <Repeat size={14} />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Histórico"
                          onClick={() => setHistTarget(r)} data-testid={`resp-history-btn-${r.id}`}>
                          <History size={14} />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Excluir"
                            onClick={() => remove(r)} data-testid={`resp-delete-btn-${r.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ResponsibleDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing}
        responsibles={list} tree={tree} companies={companies} onSaved={load} />
      <SubstituteDialog open={!!subTarget} onOpenChange={(v) => !v && setSubTarget(null)} target={subTarget}
        responsibles={list} onSaved={load} />
      <HistoryDialog open={!!histTarget} onOpenChange={(v) => !v && setHistTarget(null)} target={histTarget} />
    </div>
  );
}
